/**
 * Unified Recommended Accessories Module
 *
 * This module provides a single source of truth for rendering recommended accessories
 * on product pages. It supports both metafield-driven data and fallback data-ids,
 * and can render in vertical (sidebar) or horizontal (below product) layouts.
 */

import { normalizeAccessories, signatureFromAccessories, fetchAccessoryBySkuFlexible } from './accessory-data';
import { renderAccessory } from './accessory-render';
import createLogger from '../../common/utils/logger';

/**
 * Render recommended accessories with unified logic
 *
 * @param {Object} options - Configuration options
 * @param {Array|string} options.accessories - Array of SKUs or comma-separated string from metafields (primary source)
 * @param {string} options.dataIds - Comma-separated SKUs from data-ids attribute (fallback source)
 * @param {string} options.token - Authorization token for GraphQL requests
 * @param {string} options.orientation - Layout orientation: 'vertical' or 'horizontal'
 * @param {Object} options.context - Product details context (for fallback addProductToCart, etc.)
 * @returns {Promise<Object>} Result object with ok status and reason
 */
export async function renderRecommendedAccessories(options = {}) {
    const {
        accessories = null,
        dataIds = null,
        token = null,
        orientation = 'vertical',
        context = null
    } = options;

    const REG = window.__recAccREG || (window.__recAccREG = {
        inFlight: false,
        lastSig: null,
        currentId: null,
        abortController: null,
    });

    const callId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
    const log = createLogger('recAcc-unified', callId);
    log.group('start');

    try {
        const accEl = document.getElementById('recommended_accessories');
        if (!accEl) {
            log.warn('Container #recommended_accessories not found—bailing.');
            return { ok: false, reason: 'no-container' };
        }

        // Determine data source: metafields first, then fallback to data-ids
        let skus = [];
        let dataSource = 'none';
        let skusDefault = [];
        if (accessories && (Array.isArray(accessories) || typeof accessories === 'string')) {
            skus = normalizeAccessories(accessories);
            if (skus.length > 0) {
                dataSource = 'metafields';
                log.info('Using metafields as data source', { count: skus.length });
            }
        }

        // Fallback to data-ids if no metafields
        if (dataIds && dataIds !== '0') {
            skusDefault = normalizeAccessories(dataIds);
            if (skusDefault.length > 0) {
                dataSource = 'data-ids';
                log.info('Using data-ids fallback as data source', { count: skusDefault.length });
            }
        }
        
        skus = [...skus, ...skusDefault];
        

        if (!Array.isArray(skus) || skus.length === 0) {
            log.info('No accessories supplied—nothing to render.');
            // Clear container if no data
            accEl.innerHTML = '';
            delete accEl.dataset.renderedFor;
            delete accEl.dataset.orientation;
            return { ok: true, reason: 'no-accessories' };
        }

        if (!token || typeof token !== 'string') {
            log.warn('Missing/invalid token—requests may fail.');
        }

        const sig = signatureFromAccessories(skus);
        log.info('Call signature', { sig, count: skus.length, dataSource, orientation });

        // Skip if already rendered with same signature and orientation
        const alreadyRendered =
            accEl.dataset.renderedFor === sig &&
            accEl.dataset.orientation === orientation &&
            accEl.querySelector('.accessory');

        if (alreadyRendered) {
            log.info('Same signature and orientation already rendered—skipping.');
            return { ok: true, reason: 'already-rendered' };
        }

        // Single-flight: cancel previous call if any
        if (REG.abortController) {
            REG.abortController.abort('superseded-by-new-call');
            log.debug('Aborted previous in-flight call');
        }
        const abortController = new AbortController();
        REG.abortController = abortController;
        REG.inFlight = true;
        REG.currentId = callId;
        REG.lastSig = sig;

        // Handle positioning based on orientation
        let targetContainer = accEl;
        const productViewSection = document.getElementById('product-view-section');

        if (orientation === 'horizontal' && productViewSection) {
            // For horizontal layout, we'll move the container after product-view-section
            // Check if container is already positioned correctly
            const isAfterProductView = productViewSection.nextElementSibling === accEl;

            if (!isAfterProductView) {
                log.info('Moving container for horizontal layout');
                // Insert after product-view-section
                if (accEl.parentNode) {
                    accEl.parentNode.removeChild(accEl);
                }
                productViewSection.parentNode.insertBefore(accEl, productViewSection.nextSibling);
            }

            // Add horizontal class
            accEl.classList.remove('RecommendedAccessories--vertical');
            accEl.classList.add('RecommendedAccessories--horizontal');
        } else {
            // Vertical layout - ensure it's in the original position (inside .buy-section)
            // The container should already be there from the template
            accEl.classList.remove('RecommendedAccessories--horizontal');
            accEl.classList.add('RecommendedAccessories--vertical');
        }

        // Clear content
        accEl.innerHTML = '';

        // Parallel fetch; preserve order in render
        log.pmark('Starting fetches');
        const results = await Promise.allSettled(
            skus.map((sku) => fetchAccessoryBySkuFlexible(sku, token, abortController.signal, log))
        );
        log.pmark('Fetches complete', { count: results.length });

        // Stale-response guard
        if (REG.currentId !== callId) {
            log.warn('Stale response (newer call exists)—skipping render.');
            return { ok: false, reason: 'stale-response' };
        }

        // Log per-SKU outcome
        const rows = results.map((r, idx) => {
            if (r.status === 'fulfilled') {
                return {
                    order: idx + 1,
                    sku: r.value?.sku,
                    ok: true,
                    ms: r.value?.timingMs ?? null,
                    hasProduct: !!r.value?.product,
                };
            }
            return {
                order: idx + 1,
                sku: skus[idx],
                ok: false,
                ms: null,
                error: (r.reason && (r.reason.message || String(r.reason))) || 'unknown',
            };
        });

        try {
            console.table(rows);
        } catch {
            log.debug('Rows', rows);
        }

        // Render in order
        let renderedCount = 0;
        results.forEach((r, i) => {
            if (r.status !== 'fulfilled') {
                log.warn(`Skipped render for ${skus[i]} due to fetch error`, r.reason);
                return;
            }
            const { sku, product } = r.value;
            if (!product) {
                log.warn(`No product returned for ${sku}—not rendering.`);
                return;
            }
            try {
                //console.debug('Rendering', { sku, product });
                renderAccessory(accEl, product, skus[i], i, log);
                renderedCount += 1;
            } catch (err) {
                log.error(`Render failed for ${sku}`, err);
            }
        });

        // Only after we know we have at least one card, add the heading and mark signature
        if (renderedCount > 0) {
            const heading = document.createElement('h4');
            heading.id = 'accessories__heading';
            heading.classList.add('accessories__heading', 'margin-remove');
            heading.innerText = window.BC_CONTEXT.pdp_accessories_heading ||'Recommended Accessories';
            accEl.prepend(heading);

            accEl.dataset.renderedFor = sig;
            accEl.dataset.orientation = orientation;

            // Handle carousel initialization/destruction based on item count
            if (orientation === 'horizontal') {
                handleCarousel(accEl, renderedCount, log);
            }

            log.info(`Rendered ${renderedCount} accessories in ${orientation} layout from ${dataSource}`);
        } else {
            // Nothing rendered → clear any stale state
            accEl.innerHTML = '';
            delete accEl.dataset.renderedFor;
            delete accEl.dataset.orientation;
            log.info('No accessories rendered—cleared container');
        }

        return { ok: true, reason: 'rendered', count: renderedCount, dataSource, orientation };

    } catch (error) {
        console.error('[recAcc-unified] Unhandled error in renderRecommendedAccessories', error);
        return { ok: false, reason: 'exception', error };
    } finally {
        if (REG.currentId === callId) {
            REG.inFlight = false;
            REG.abortController = null;
        }
        log.groupEnd();
    }
}


/**
 * Handle carousel state - initialize or destroy based on item count
 * @param {HTMLElement} container - The accessories container
 * @param {number} itemCount - Number of accessory items
 * @param {Object} log - Logger object
 */
function handleCarousel(container, itemCount, log) {
    const $container = window.jQuery ? window.jQuery(container) : window.$(container);
    const hasCarousel = $container.find('.slick-initialized').length > 0;
    const needsCarousel = itemCount > 4;

    if (needsCarousel && !hasCarousel) {
        // Need carousel but don't have one - initialize it
        log.info(`Initializing carousel for ${itemCount} accessories`);
        initializeCarousel(container, log);
    } else if (!needsCarousel && hasCarousel) {
        // Have carousel but don't need it - destroy it
        log.info(`Destroying carousel (only ${itemCount} accessories)`);
        destroyCarousel(container, log);
    } else if (needsCarousel && hasCarousel) {
        // Already have carousel and still need it - refresh it
        log.info(`Refreshing carousel for ${itemCount} accessories`);
        destroyCarousel(container, log);
        initializeCarousel(container, log);
    } else {
        // Don't need carousel and don't have one - ensure grid layout
        log.info(`Using grid layout for ${itemCount} accessories`);
        ensureGridLayout(container);
    }
}

/**
 * Destroy existing Slick carousel and restore grid layout
 * @param {HTMLElement} container - The accessories container
 * @param {Object} log - Logger object
 */
function destroyCarousel(container, log) {
    try {
        const $container = window.jQuery ? window.jQuery(container) : window.$(container);
        const $carousel = $container.find('.slick-initialized');

        if ($carousel.length > 0) {
            // Destroy slick instance
            $carousel.slick('unslick');

            // Unwrap carousel containers
            $container.find('.accessory').unwrap('.accessories-carousel').unwrap('.container');

            // Restore grid layout
            ensureGridLayout(container);

            log.info('Carousel destroyed successfully');
        }
    } catch (err) {
        log.error(`Failed to destroy carousel: ${err.message}`);
    }
}

/**
 * Ensure grid layout is properly set
 * @param {HTMLElement} container - The accessories container
 */
function ensureGridLayout(container) {
    container.style.display = 'grid';
    container.style.gridTemplateColumns = '';
    container.style.gap = '';
}

/**
 * Initialize Slick carousel for horizontal accessories layout
 * @param {HTMLElement} container - The accessories container
 * @param {Object} log - Logger object
 */
function initializeCarousel(container, log) {
    // Wait for next tick to ensure DOM is ready
    setTimeout(() => {
        const $container = window.jQuery ? window.jQuery(container) : window.$(container);

        if (!$container || !$container.length) {
            log.warn('Container not found for carousel initialization');
            return;
        }

        // Check if slick is available
        if (typeof $container.slick !== 'function') {
            log.warn('Slick carousel library not available');
            return;
        }

        try {
            // Remove grid layout, carousel will handle it
            container.style.display = 'block';
            container.style.gridTemplateColumns = '';
            container.style.gap = '';

            // Get all accessory cards except the heading
            const $cards = $container.find('.accessory');

            // Wrap cards in a carousel container if not already wrapped
            if (!$container.find('.accessories-carousel').length) {
                $cards.wrapAll('<div class="container"><div class="accessories-carousel"></div></div>');
            }
            
            const $carousel = $container.find('.accessories-carousel');

            // Initialize slick carousel
            $carousel.slick({
                dots: false,
                infinite: false,
                speed: 300,
                slidesToShow: 5,
                slidesToScroll: 5,
                variableWidth: false, // Ensure equal width slides
                centerMode: false, // Disable center mode to prevent partial slides
                responsive: [
                    {
                        breakpoint: 1280, // large desktop
                        settings: {
                            slidesToShow: 5,
                            slidesToScroll: 5
                        }
                    },
                    {
                        breakpoint: 1024, // desktop
                        settings: {
                            slidesToShow: 4,
                            slidesToScroll: 4
                        }
                    },
                    {
                        breakpoint: 768, // tablet
                        settings: {
                            slidesToShow: 3,
                            slidesToScroll: 3
                        }
                    },
                    {
                        breakpoint: 576, // small tablet
                        settings: {
                            slidesToShow: 2,
                            slidesToScroll: 2
                        }
                    },
                    {
                        breakpoint: 480, // mobile
                        settings: {
                            slidesToShow: 1,
                            slidesToScroll: 1
                        }
                    }
                ]
            });

            log.info('Carousel initialized successfully');
        } catch (err) {
            log.error(`Failed to initialize carousel: ${err.message}`);
        }
    }, 100);
}
